/**
 * Quick View (QV) — UPDATED
 * Changes:
 * - No default variant selection on open
 * - ATC click without selection shows: "Please Select a Size first"
 * - Price updates live from variant JSON once selection is valid
 * - Buy Now hidden until valid variant selected
 */

(() => {
  const overlay = document.getElementById('qv-overlay');
  const content = document.getElementById('qv-content');
  if (!overlay || !content) return;

  let lastFocus = null;
  const SECTION_IDS = ['cart-drawer', 'cart-icon-bubble'];

  // iOS-safe scroll lock
  let qvScrollY = 0;

  const lockScroll = () => {
    qvScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('qv-scroll-locked');
    document.body.classList.add('qv-scroll-locked');
    document.body.style.top = `-${qvScrollY}px`;
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove('qv-scroll-locked');
    document.body.classList.remove('qv-scroll-locked');

    const top = document.body.style.top;
    document.body.style.top = '';

    const restoreY = qvScrollY || (top ? Math.abs(parseInt(top, 10)) : 0);
    window.scrollTo(0, restoreY);
  };

  const openOverlay = () => {
    lastFocus = document.activeElement;
    overlay.hidden = false;
    lockScroll();
    overlay.querySelector('.qv-modal')?.focus();
  };

  const closeOverlayHard = () => {
    overlay.hidden = true;
    unlockScroll();
    content.innerHTML = `<div class="qv-loading">Loading…</div>`;
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  };

  const openCartDrawer = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return true;
    }

    const drawerEl =
      document.querySelector('#CartDrawer') ||
      document.querySelector('.cart-drawer') ||
      document.querySelector('[data-cart-drawer]');

    if (drawerEl) {
      drawerEl.classList.add('is-open');
      drawerEl.setAttribute('open', 'open');
      return true;
    }

    const toggles = [
      '[data-cart-drawer-toggle]',
      '[data-cart-toggle]',
      'button[aria-controls*="CartDrawer"]',
      '.header__icon--cart',
      '.site-header__cart'
    ];

    for (const sel of toggles) {
      const el = document.querySelector(sel);
      if (el) {
        el.click();
        return true;
      }
    }

    return false;
  };

  const refreshCartSectionsSafe = async () => {
    const url = `/?sections=${encodeURIComponent(SECTION_IDS.join(','))}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart sections');
    const sections = await res.json();

    // cart drawer
    const drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      const doc = new DOMParser().parseFromString(drawerHTML, 'text/html');

      const incomingCartDrawer =
        doc.querySelector('cart-drawer') ||
        doc.querySelector('#CartDrawer') ||
        doc.querySelector('.cart-drawer') ||
        doc.body.firstElementChild;

      const liveCartDrawerComponent = document.querySelector('cart-drawer');
      const liveCartDrawerWrapper =
        document.querySelector('#CartDrawer') ||
        document.querySelector('.cart-drawer') ||
        document.querySelector('[data-cart-drawer]');

      if (liveCartDrawerComponent && incomingCartDrawer) {
        const incomingInner =
          incomingCartDrawer.querySelector('.drawer__inner') ||
          incomingCartDrawer.querySelector('#CartDrawer-Form') ||
          incomingCartDrawer;

        const liveInner =
          liveCartDrawerComponent.querySelector('.drawer__inner') ||
          liveCartDrawerComponent.querySelector('#CartDrawer-Form') ||
          liveCartDrawerComponent;

        if (incomingInner && liveInner) liveInner.innerHTML = incomingInner.innerHTML;
        else liveCartDrawerComponent.innerHTML = incomingCartDrawer.innerHTML;
      } else if (liveCartDrawerWrapper && incomingCartDrawer) {
        const incomingInner =
          incomingCartDrawer.querySelector('.drawer__inner') ||
          incomingCartDrawer.querySelector('#CartDrawer-Form') ||
          incomingCartDrawer;

        const liveInner =
          liveCartDrawerWrapper.querySelector('.drawer__inner') ||
          liveCartDrawerWrapper.querySelector('#CartDrawer-Form') ||
          liveCartDrawerWrapper;

        if (incomingInner && liveInner) liveInner.innerHTML = incomingInner.innerHTML;
        else liveCartDrawerWrapper.innerHTML = incomingCartDrawer.innerHTML || incomingCartDrawer.outerHTML;
      }
    }

    // cart icon bubble
    const bubbleHTML = sections['cart-icon-bubble'];
    if (bubbleHTML) {
      const doc = new DOMParser().parseFromString(bubbleHTML, 'text/html');

      const incomingSection =
        doc.querySelector('#shopify-section-cart-icon-bubble') || doc.body.firstElementChild;
      const liveSection = document.querySelector('#shopify-section-cart-icon-bubble');

      if (incomingSection && liveSection) {
        liveSection.outerHTML = incomingSection.outerHTML;
      } else {
        const incomingBubble = doc.querySelector('#cart-icon-bubble') || doc.querySelector('cart-icon-bubble');
        const liveBubble = document.querySelector('#cart-icon-bubble') || document.querySelector('cart-icon-bubble');

        if (
          incomingBubble &&
          liveBubble &&
          incomingBubble.tagName &&
          liveBubble.tagName &&
          incomingBubble.tagName.toLowerCase() === liveBubble.tagName.toLowerCase()
        ) {
          liveBubble.outerHTML = incomingBubble.outerHTML;
        }
      }
    }

    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  };

  // ---- Money formatting + live price ----
  const getMoneyFormat = () =>
    window.Shopify?.money_format ||
    window.theme?.moneyFormat ||
    window.theme?.money_format ||
    '${{amount}}';

  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, getMoneyFormat());
    return (Number(cents || 0) / 100).toFixed(2);
  };

  const renderQVPriceHTML = (variant) => {
    if (!variant) return '';
    const price = typeof variant.price === 'number' ? variant.price : 0;
    const compare = typeof variant.compare_at_price === 'number' ? variant.compare_at_price : 0;
    const onSale = compare > price;

    if (onSale) {
      return `
        <div class="price price--large">
          <span class="price-item price-item--sale">${formatMoney(price)}</span>
          <span class="price-item price-item--regular" style="text-decoration:line-through; opacity:.6; margin-left:8px;">
            ${formatMoney(compare)}
          </span>
        </div>
      `.trim();
    }

    return `
      <div class="price price--large">
        <span class="price-item price-item--regular">${formatMoney(price)}</span>
      </div>
    `.trim();
  };

  const updatePriceLive = (root, variant) => {
    const wrap = root.querySelector('.qv-price');
    if (!wrap) return;
    // if no variant selected, keep whatever price you want. Here: blank.
    wrap.innerHTML = variant ? renderQVPriceHTML(variant) : '';
  };

  // ---- Warning helper ----
  const getWarnEl = (root) => {
    let el = root.querySelector('[data-qv-warning]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'qv-warning';
      el.setAttribute('data-qv-warning', '');
      el.setAttribute('aria-live', 'polite');
      el.hidden = true;
      const form = root.querySelector('form[action^="/cart/add"]');
      form?.prepend(el);
    }
    return el;
  };

  const showWarning = (root, msg) => {
    const el = getWarnEl(root);
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(showWarning._t);
    showWarning._t = setTimeout(() => {
      el.hidden = true;
      el.textContent = '';
    }, 2500);
  };

  const hideWarning = (root) => {
    const el = root.querySelector('[data-qv-warning]');
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  };

  // ---- Variant helpers ----
  const getOptionIndexes = (root) => {
    const els = [...root.querySelectorAll('[data-qv-option]')];
    const idxs = els
      .map((el) => parseInt(el.getAttribute('data-index') || '0', 10))
      .filter((n) => !Number.isNaN(n));
    return [...new Set(idxs)].sort((a, b) => a - b);
  };

  const getSelectedOptions = (root) => {
    const idxs = getOptionIndexes(root);
    if (!idxs.length) return [];

    return idxs.map((idx) => {
      const radioChecked = root.querySelector(
        `input[type="radio"][data-qv-option][data-index="${idx}"]:checked`
      );
      if (radioChecked) return radioChecked.value;

      const sel = root.querySelector(`select[data-qv-option][data-index="${idx}"]`);
      if (sel) return sel.value;

      return '';
    });
  };

  const allSelected = (opts) => Array.isArray(opts) && opts.length && opts.every((v) => (v || '').trim() !== '');

  const findVariant = (variants, opts) =>
    variants.find(
      (v) =>
        Array.isArray(v.options) &&
        v.options.length === opts.length &&
        v.options.every((o, i) => o === opts[i])
    );

  const setAtcNoSelection = (root, form) => {
    const idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = '';

    const atcBtn = root.querySelector('.qv-btn--atc');
    if (atcBtn) {
      atcBtn.disabled = false; // allow click so we can show warning
      atcBtn.textContent = 'Add to Cart';
    }

    const buyNow = root.querySelector('.qv-buy-now');
    if (buyNow) buyNow.style.display = 'none';

    updatePriceLive(root, null);
  };

  const setAtcWithVariant = (root, form, variant) => {
    const idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;

    const atcBtn = root.querySelector('.qv-btn--atc');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? 'Add to Cart' : 'Sold out';
    }

    const buyNow = root.querySelector('.qv-buy-now');
    if (buyNow) buyNow.style.display = '';

    updatePriceLive(root, variant);
  };

  const computeAvailabilityMap = (variants, selected) => {
    const map = {};
    const optionCount = selected.length;

    for (let pos = 0; pos < optionCount; pos++) {
      map[pos] = {};
      const values = new Set(variants.map((v) => (v.options || [])[pos]).filter(Boolean));

      values.forEach((val) => {
        const ok = variants.some((v) => {
          if (!v.available) return false;
          if (!Array.isArray(v.options)) return false;

          for (let i = 0; i < optionCount; i++) {
            const want = i === pos ? val : selected[i];
            if (!want) return true; // if other selections missing, don't block
            if (v.options[i] !== want) return false;
          }
          return true;
        });

        map[pos][val] = ok;
      });
    }

    return map;
  };

  const applyAvailability = (root, variants, selected) => {
    // If nothing selected yet, DO NOT disable anything.
    if (!allSelected(selected)) {
      root.querySelectorAll('input[type="radio"][data-qv-option]').forEach((input) => {
        input.disabled = false;
        const label = root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (label) label.classList.remove('is-disabled');
      });
      root.querySelectorAll('select[data-qv-option]').forEach((sel) => {
        [...sel.options].forEach((opt) => (opt.disabled = false));
      });
      return;
    }

    const map = computeAvailabilityMap(variants, selected);

    root.querySelectorAll('input[type="radio"][data-qv-option]').forEach((input) => {
      const pos = parseInt(input.getAttribute('data-index') || '0', 10);
      const val = input.value;
      const ok = map[pos] ? map[pos][val] !== false : true;

      input.disabled = !ok;

      const label = root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (label) label.classList.toggle('is-disabled', !ok);
    });

    root.querySelectorAll('select[data-qv-option]').forEach((sel) => {
      const pos = parseInt(sel.getAttribute('data-index') || '0', 10);
      [...sel.options].forEach((opt) => {
        const ok = map[pos] ? map[pos][opt.value] !== false : true;
        opt.disabled = !ok;
      });
    });
  };

  const applyVariantImage = (root, variant) => {
    const mainImg = root.querySelector('[data-qv-main-img]');
    if (!mainImg) return;

    const src =
      variant?.featured_image?.src ||
      variant?.featured_media?.preview_image?.src ||
      null;

    if (src) mainImg.src = src;
  };

  const clearSelections = (root) => {
    root.querySelectorAll('input[type="radio"][data-qv-option]').forEach((r) => (r.checked = false));
    // if you ever use selects, try set to first option
    root.querySelectorAll('select[data-qv-option]').forEach((s) => (s.selectedIndex = 0));

    // clear visible selected labels
    root.querySelectorAll('[data-qv-selected-value]').forEach((el) => (el.textContent = ''));
  };

  // ---------- Quick View wiring ----------
  const wireQV = (root) => {
    const inner = root.querySelector('.qv-inner') || root;
    const variantsEl = inner.querySelector('[data-qv-variants]');
    const form = inner.querySelector('form[action^="/cart/add"]');
    if (!variantsEl || !form) return;

    const variants = JSON.parse(variantsEl.textContent || '[]');

    // Ensure warning element exists
    getWarnEl(inner);

    // Qty buttons
    inner.querySelectorAll('[data-qv-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = inner.querySelector('input[name="quantity"]');
        if (!input) return;
        const current = parseInt(input.value || '1', 10);
        const next = btn.dataset.qvQty === 'inc' ? current + 1 : current - 1;
        input.value = String(Math.max(1, next));
      });
    });

    // Thumbnail -> main image
    inner.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qv-thumb]');
      if (!btn) return;
      const url = btn.getAttribute('data-qv-img');
      const mainImg = inner.querySelector('[data-qv-main-img]');
      if (mainImg && url) mainImg.src = url;
    });

    // Style variants inside QV
    inner.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-sv-item][data-qv-handle]');
      if (!btn) return;
      const handle = btn.getAttribute('data-qv-handle');
      if (!handle || btn.disabled) return;
      loadProduct(handle);
    });

    // Variant changes
    const onVariantUIChange = () => {
      hideWarning(inner);

      const opts = getSelectedOptions(inner);

      // Not all selected yet -> keep id empty
      if (!allSelected(opts)) {
        setAtcNoSelection(inner, form);
        applyAvailability(inner, variants, opts);
        return;
      }

      let match = findVariant(variants, opts);

      // If invalid combo after full selection -> snap to first available
      if (!match) match = variants.find((v) => v.available) || variants[0];

      setAtcWithVariant(inner, form, match);
      applyVariantImage(inner, match);
      applyAvailability(inner, variants, getSelectedOptions(inner));
    };

    inner.querySelectorAll('[data-qv-option]').forEach((el) => {
      el.addEventListener('change', onVariantUIChange);
    });

    // AJAX ATC (with warning if no variant selected)
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const idInput = form.querySelector('input[name="id"]');
      const variantId = idInput?.value;

      if (!variantId) {
        // You specifically asked this exact text:
        showWarning(inner, 'Please Select a Size first');
        return;
      }

      const submitBtn =
        form.querySelector('button[type="submit"][name="add"]') || inner.querySelector('.qv-btn--atc');

      try {
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-loading');
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          console.error('Add to cart failed:', err || res.statusText);
          return;
        }

        await refreshCartSectionsSafe();
        closeOverlayHard();
        openCartDrawer();
      } catch (err) {
        console.error(err);
        closeOverlayHard();
        openCartDrawer();
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
        }
      }
    });

    // INIT: no default selection
    clearSelections(inner);
    setAtcNoSelection(inner, form);
    applyAvailability(inner, variants, getSelectedOptions(inner));

    if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
  };

  const loadProduct = async (handle) => {
    content.innerHTML = `<div class="qv-loading">Loading…</div>`;
    openOverlay();

    try {
      const res = await fetch(`/products/${handle}?section_id=quick-view`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!res.ok) throw new Error(`QV fetch failed: ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.qv-inner');
      if (!inner) throw new Error('Quick view markup not found (.qv-inner)');

      content.innerHTML = inner.outerHTML;
      wireQV(content);
    } catch (err) {
      console.error(err);
      closeOverlayHard();
    }
  };

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('.js-qv-open');
    if (openBtn) {
      const handle = openBtn.dataset.handle;
      if (handle) loadProduct(handle);
      return;
    }

    if (e.target.matches('[data-qv-close]') || e.target.closest('[data-qv-close]')) {
      closeOverlayHard();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!overlay.hidden && e.key === 'Escape') closeOverlayHard();
  });
})();